// utils.js — shared helpers for the Server Management module
import axios from "axios";

export const postForm = (url, data) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        params.append(key, value);
    });
    return axios.post(url, params);
};

export const getEmployee = () => {
    try {
        return JSON.parse(localStorage.getItem("employee")) || {};
    } catch {
        return {};
    }
};

export const getActionTakenBy = () => {
    try {
        // Try to get from 'employee' key first, fallback to 'user'
        let userData = localStorage.getItem('employee');
        if (!userData) {
            userData = localStorage.getItem('user');
        }
        const user = JSON.parse(userData || '{}');
        return user.employee_name || user.username || "Unknown";
    } catch {
        return "Unknown";
    }
};
// NOTE (security): this trusts a client-side flag. Replace with a real
// server-resolved role check before production — see migration.sql.
export const isAdminUser = () => {
    try {
        // Try to get from 'employee' key first, fallback to 'user'
        let userData = localStorage.getItem('employee');
        if (!userData) {
            userData = localStorage.getItem('user');
        }
        const user = JSON.parse(userData || '{}');
        return user.privilages === 2;
    } catch {
        return false;
    }
};

export const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

export const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
};

export const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const CURRENCY_SYMBOLS = { INR: "₹", BDT: "৳", AED: "د.إ", MYR: "RM", USD: "$", EUR: "€", GBP: "£" };

export const formatMoney = (amount, currency = "INR") => {
    const symbol = CURRENCY_SYMBOLS[currency] || "";
    const n = Number(amount) || 0;
    return `${symbol}${n.toLocaleString("en-IN")}`;
};

// Parses "512GB" / "512 GB" / "0.5TB" / "512" into a GB number for charting
// and for the server cost auto-calc (rate-per-GB * ROM).
export const parseToGB = (value) => {
    if (!value) return 0;
    const str = String(value).trim().toUpperCase();
    const match = str.match(/([\d.]+)\s*(TB|GB|MB)?/);
    if (!match) return 0;
    const num = parseFloat(match[1]) || 0;
    const unit = match[2] || "GB";
    if (unit === "TB") return num * 1024;
    if (unit === "MB") return num / 1024;
    return num;
};

// "Expire Date" / "Next Renewal Date" billing-cycle rule used throughout
// the renewal table: day 1–14 snaps to the 1st of that month, day 15–31
// snaps to the 15th. Mirrors normalizeBillingDate() in renewalHelpers.php.
export const normalizeBillingDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const day = d.getDate();
    const anchor = day >= 1 && day <= 14 ? 1 : 15;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}-${String(anchor).padStart(2, "0")}`;
};

// Turns a subscription value ("1".."12", legacy "3 months") into months.
export const subscriptionToMonths = (subscription) => {
    if (!subscription) return 1;
    const match = String(subscription).match(/(\d+)/);
    const months = match ? parseInt(match[1], 10) : 1;
    return months > 0 ? months : 1;
};

// total amount = (rate * userCount * months) - discount
export const computeTotalAmount = (rate, subscription, discount, userCount = 1) => {
    const r = Number(rate) || 0;
    const months = subscriptionToMonths(subscription);
    const d = Number(discount) || 0;
    const uc = Math.max(1, Number(userCount) || 1);
    const total = r * uc * months - d;
    return total > 0 ? Math.round(total * 100) / 100 : 0;
};

export const monthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-IN", { year: "numeric", month: "long" });
};

export const providerOptions = [
    { label: "Krish Communication(Jittu Da)", value: "Krish Communication(Jittu Da)" },
    { label: "i2k2 Networks pvt. ltd.", value: "i2k2 Networks pvt. ltd." },
];

export const currencyOptions = [
    { label: "INR (₹)", value: "INR" },
    { label: "BDT (৳)", value: "BDT" },
    { label: "AED (د.إ)", value: "AED" },
    { label: "MYR (RM)", value: "MYR" },
    { label: "USD ($)", value: "USD" },
    { label: "EUR (€)", value: "EUR" },
    { label: "GBP (£)", value: "GBP" },
];

export const licenceOptions = [
    { label: "Shared", value: "shared" },
    { label: "Dedicated", value: "dedicated" },
    { label: "Trial", value: "trial" },
];

export const statusOptions = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
    { label: "Pending", value: "Pending" },
    { label: "Expired", value: "Expired" },
];

// 1–12 months, used by every searchable "Subscription" dropdown.
export const subscriptionMonthOptions = Array.from({ length: 12 }, (_, i) => {
    const n = i + 1;
    return { label: `${n} ${n === 1 ? "Month" : "Months"}`, value: String(n) };
});

export const EMPTY_SERVER_FORM = {
    server_port: "",
    server_name: "",
    provider_name: "",
    server_user_id: "",
    server_password: "",
    ram: "",
    rom: "",
    cost_price: "",
};

// One "user details" block — repeated for every user added in the form.
export const EMPTY_USER_ENTRY = {
    user_name: "",
    user_password: "",
    licence_type: "",
    actual_user_name: "",
    actual_user_password: "",
    activation_date: "",
    expire_date: "",
    next_renewal_date: "",
    prorata_expire_date: "",
    rate: "",
    discount: "",
    currency: "",
    subscription: "1",
    gateway_path: "",
};

// Company-level fields (top of the form) + a list of user blocks.
export const EMPTY_USER_FORM = {
    // user_id intentionally omitted — the backend auto-generates it.
    company_name: "",
    contact_person_name: "",
    contact_person_number: "",
    server_port: "",
    server_name: "",
    sell_person_id: "",
    sell_person_name: "",
    region: "",
    referred_person: "",
    referred_person_number: "",
    remarks: "",
    reason: "",
    status: "Active",
    users: [{ ...EMPTY_USER_ENTRY }],
};